import axios from "axios";
import { useEffect, useState } from "react";
import CardProductDescription from "../components/CardProductDescription";
import Error from "../components/Error";
import ReviseProductDescription from "../components/ReviseProductDescription";
import TranslateDropdown from "../components/TranslateDropdown";
import {
  DIRECT_TRANSLATION,
  DIRECT_TRANSLATION_URL,
  GENERATE_PROMT_PRODUCT_DESCRIPTION,
} from "../utils/constants";
import { getIdToken } from "../utils/translateHelper";
import ProductCardWithInput from "../components/ProductDescription/ProductCardWithInput";
import ThinBorder from "../components/ThinBorder";
import CustomModal from "../components/Shared/CustomModal";
import WarningIcon from "@mui/icons-material/Warning";

const ProductDescription = ({
  setSectionData,
  sectionData,
  activeTab,
  errorMessage,
  setErrorMessage,
  isLoading,
  translateLanguage,
  setSourceLanguage,
  setTranslateLanguage,
  sourceLanguage,
  directTranslate,
  setInputText,
  handleTranslateLanguage,
  setSelectedLanguage,
  titles,
  selectedTitle,
  setIsProductDescriptionLoading,
  productDescriptionContentLocked,
  setProductDescriptionContentLocked,
  file,
  setFile,
  imagePreview,
  setImagePreview,
  dropRef,
  fileInputRef,
  productDescriptionState,
  setProductDescriptionState,
  isSidebarOpen,
}) => {
  const [isFileTypeModalOpen, setIsFileTypeModalOpen] = useState(false);
  const extractAndConcatenateStrings = (obj) => {
    return {
      title: obj.title,
      description: obj.description,
      bulletPoints: obj.bulletPoints.join(" ||| "),
    };
  };

  const reconstructJson = (originalObj, translatedStrings) => {
    return {
      ...originalObj,
      title: translatedStrings.title,
      description: translatedStrings.description,
      bulletPoints: translatedStrings.bulletPoints.split(" ||| "),
    };
  };

  const translateText = async (text, sourceLang, targetLang) => {
    setIsProductDescriptionLoading(true);
    try {
      const idToken = await getIdToken(setErrorMessage);
      let payload = {
        source_language: sourceLang,
        target_language: targetLang,
        input: text,
        translation_model: "Google Translate",
        localization_model: "GPT-4",
      };

      const response = await axios.post(DIRECT_TRANSLATION_URL, payload, {
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          Authorization: idToken,
        },
      });
      setIsProductDescriptionLoading(false);
      return response.data.translation;
    } catch (error) {
      setErrorMessage(error.message);
      setIsProductDescriptionLoading(false);
      throw error;
    }
  };

  const translateJsonObject = async (obj, sourceLang, targetLang) => {
    const stringsToTranslate = extractAndConcatenateStrings(obj);

    const translatedStrings = {
      title: await translateText(
        stringsToTranslate.title,
        sourceLang,
        targetLang
      ),
      description: await translateText(
        stringsToTranslate.description,
        sourceLang,
        targetLang
      ),
      bulletPoints: await translateText(
        stringsToTranslate.bulletPoints,
        sourceLang,
        targetLang
      ),
    };

    return reconstructJson(obj, translatedStrings);
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files);
    const acceptedFileTypes = [".jpg", ".png", ".jpeg", ".webp"];
    const filteredFiles = files.filter((file) =>
      acceptedFileTypes.some((type) => file.name.endsWith(type))
    );
    if (filteredFiles.length !== files.length) {
      setIsFileTypeModalOpen(true);
      return;
    }

    if (filteredFiles.length > 0) {
      const file = filteredFiles[0];
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const [shouldTranslate, setShouldTranslate] = useState(true);

  useEffect(() => {
    const handleTranslation = async () => {
      if (!shouldTranslate) {
        setShouldTranslate(true);
        return;
      }

      if (sourceLanguage[activeTab][0] !== translateLanguage[activeTab][0]) {
        let translatedData;
        if (sectionData.productDescriptionTranslate?.bulletPoints?.length > 0) {
          translatedData = await translateJsonObject(
            sectionData.productDescriptionTranslate,
            sourceLanguage[activeTab][selectedTitle],
            translateLanguage[activeTab][selectedTitle]
          );
        } else {
          directTranslate(
            sectionData.productDescription,
            sourceLanguage[activeTab][selectedTitle],
            translateLanguage[activeTab][selectedTitle],
            activeTab
          );
        }

        setSourceLanguage((prev) => ({
          ...prev,
          [activeTab]: translateLanguage[activeTab],
        }));

        setSectionData((prevState) => {
          const sectionDataCopy = { ...prevState };
          if (sectionDataCopy.productDescriptionTranslate) {
            sectionDataCopy.productDescriptionTranslate = translatedData;
          }
          return sectionDataCopy;
        });
      }
    };

    handleTranslation();
  }, [translateLanguage[activeTab]]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const acceptedFileTypes = [".jpg", ".png", ".jpeg", ".webp"];
    const filteredFiles = files.filter((file) =>
      acceptedFileTypes.some((type) => file.name.endsWith(type))
    );
    if (filteredFiles.length !== files.length) {
      setIsFileTypeModalOpen(true);
      return;
    }
    if (filteredFiles.length > 0) {
      const file = filteredFiles[0];
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    let div = dropRef.current;

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files);
      const acceptedFileTypes = [".jpg", ".png", ".jpeg", ".webp"];
      const filteredFiles = files.filter((file) =>
        acceptedFileTypes.some((type) => file.name.endsWith(type))
      );
      if (filteredFiles.length !== files.length) {
        setIsFileTypeModalOpen(true);
        return;
      }
      if (filteredFiles.length > 0) {
        const file = filteredFiles[0];
        setFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    };

    div?.addEventListener("dragenter", handleDragEnter);
    div?.addEventListener("dragover", handleDragOver);
    div?.addEventListener("dragleave", handleDragLeave);
    div?.addEventListener("drop", handleDrop);

    return () => {
      div?.removeEventListener("dragenter", handleDragEnter);
      div?.removeEventListener("dragover", handleDragOver);
      div?.removeEventListener("dragleave", handleDragLeave);
      div?.removeEventListener("drop", handleDrop);
    };
  }, []);

  const CancelIcon = () => {
    setFile(null);
    setImagePreview(null);
    setProductDescriptionState({
      brand_name: "",
      product_name: "",
      product_description: "",
      collection: "",
      text_format: "",
      character_limit: "",
      platform: "",
      platform: "",
    });
    setSectionData((previousState) => ({
      ...previousState,
      productDescription: "",
    }));
  };

  const GeneratePromtProductDescription = () => {
    setIsProductDescriptionLoading(true);
    setShouldTranslate(false);
    let requestBody = {
      image: imagePreview,
      brand_name: productDescriptionState.brand_name,
      product_name: productDescriptionState.product_name,
      product_description: productDescriptionState.product_description,
      collection: productDescriptionState.collection,
      text_format: productDescriptionState.text_format,
      character_limit: productDescriptionState.character_limit,
      platform: productDescriptionState.platform.toLowerCase(),
    };
    getIdToken()
      .then((idToken) => {
        axios
          .post(GENERATE_PROMT_PRODUCT_DESCRIPTION, requestBody, {
            headers: { Authorization: idToken },
          })
          .then((response) => {
            setSectionData((previousState) => ({
              ...previousState,
              productDescription: response.data.product_description,
              productDescriptionTranslate: {},
            }));

            setIsProductDescriptionLoading(false);
          })
          .catch((error) => {
            setIsProductDescriptionLoading(false);
            setErrorMessage(error);
          });
      })
      .catch((error) => console.log(error));
  };

  return (
    <div
      className={`w-full h-max flex flex-col flex-1  px-8 py-4 m-5 bg-white rounded-s-xl ${
        isSidebarOpen ? "hidden" : "block"
      } sm:block`}
    >
      {errorMessage && <Error errorMessage={errorMessage.message} />}
      {sectionData.productDescription.length ? (
        <TranslateDropdown
          translateLanguage={translateLanguage}
          handleTranslateLanguage={handleTranslateLanguage}
          activeTab={activeTab}
          selectedTitle={selectedTitle}
        />
      ) : null}
      {file ? (
        <>
          <div
            className={`flex  flex-col sm:flex-row w-full  space-x-8  ${
              !sectionData.productDescription.length ? "h-full" : ""
            }`}
          >
            <>
              {sectionData.productDescription?.length ? (
                <ProductCardWithInput
                  text={sectionData.productDescription}
                  activeTab={activeTab}
                  setInputText={setInputText}
                  sectionData={sectionData}
                  setSelectedLanguage={setSelectedLanguage}
                  translateLanguage={translateLanguage}
                  isLoading={isLoading}
                  selectedTitle={selectedTitle}
                  setProductDescriptionContentLocked={
                    setProductDescriptionContentLocked
                  }
                  productDescriptionContentLocked={
                    productDescriptionContentLocked
                  }
                  GeneratePromtProductDescription={
                    GeneratePromtProductDescription
                  }
                  productDescriptionState={productDescriptionState}
                  setProductDescriptionState={setProductDescriptionState}
                  imagePreview={imagePreview}
                  cancelIcon={() => CancelIcon()}
                  setSectionData={setSectionData}
                  isPlatformForProductTranslate={
                    sectionData.productDescriptionTranslate?.bulletPoints
                      ?.length > 0
                      ? true
                      : false
                  }
                  productTranslateText={
                    sectionData.productDescriptionTranslate?.bulletPoints
                      ?.length > 0 && sectionData.productDescriptionTranslate
                  }
                  handleTranslateLanguage={handleTranslateLanguage}
                  setIsProductDescriptionLoading={
                    setIsProductDescriptionLoading
                  }
                />
              ) : (
                <>
                  <div className="w-full  flex justify-between">
                    <div className="w-1/2 flex justify-around gap-6 p-2">
                      <div className="h-[30rem] w-[32rem] relative my-4 rounded-lg flex-shrink-1">
                        {imagePreview && (
                          <div className="relative">
                            <img
                              src={require("../assets/Cancel_Icon_Product Description.png")}
                              alt="CancelIcon"
                              className="absolute right-0 p-3 cursor-pointer"
                              onClick={() => CancelIcon()}
                            />
                            <img
                              src={imagePreview}
                              alt="preview"
                              className="h-full w-full object-contain rounded-xl"
                            />
                          </div>
                        )}
                      </div>

                      <ThinBorder alignment="vertical" />
                    </div>
                    <div className="w-1/2 h-full">
                      <CardProductDescription
                        text={sectionData.productDescription}
                        activeTab={activeTab}
                        setInputText={setInputText}
                        sectionData={sectionData}
                        setSelectedLanguage={setSelectedLanguage}
                        translateLanguage={translateLanguage}
                        isLoading={isLoading}
                        selectedTitle={selectedTitle}
                        setProductDescriptionContentLocked={
                          setProductDescriptionContentLocked
                        }
                        productDescriptionContentLocked={
                          productDescriptionContentLocked
                        }
                        GeneratePromtProductDescription={
                          GeneratePromtProductDescription
                        }
                        productDescriptionState={productDescriptionState}
                        setProductDescriptionState={setProductDescriptionState}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          </div>
        </>
      ) : (
        <div
          ref={dropRef}
          className="flex flex-col items-center justify-center gap-7 w-full h-[50vh] border-2 border-[#DFDFDF] rounded-xl"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <img
            src={require("../assets/DragDropIcon.png")}
            alt="Drag_Drop_Icon"
          />
          <p className="text-[#53565A] font-normal">
            Drag and Drop Image here or
          </p>
          <div>
            <input
              id="file-upload"
              accept=".jpg, .png, .jpeg, .webp"
              ref={fileInputRef}
              type="file"
              onChange={onFileChange}
              hidden
              className="cursor-pointer text-blue-500"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer text-[#26890D] font-bold"
            >
              Upload
            </label>
          </div>
        </div>
      )}

      <CustomModal
        open={isFileTypeModalOpen}
        handleClose={() => setIsFileTypeModalOpen(false)}
      >
        <div className="flex flex-col items-center justify-center p-5 m-2 h-52 rounded-2xl bg-[#fff3f4]">
          <div className="p-2">
            <WarningIcon
              sx={{
                color: "red",
                fontSize: "3rem",
                width: "6rem",
                height: "6rem",
              }}
            />
          </div>
          <h2 className="p-2 font-medium text-center">
            Please Upload a valid file type.
          </h2>
          <div className="text-center font-semibold my-2">
            <p>
              Accepted file formats:{" "}
              <span className="mx-2">.jpg, .png, .jpeg, .webp</span>
            </p>
          </div>
        </div>
      </CustomModal>
    </div>
    /* </div> */
  );
};

export default ProductDescription;
